/**
 * A component that displays a header and a value.
 *
 * The header is either a React element or a string, depending on whether
 * the `renderHeader` or `header` prop is passed.
 *
 * The value is a function that returns a value to be displayed.
 *
 * The component also accepts a `wrapperClass` that gets applied to the
 * outermost `div` element. Additionally, `labelClass` and `valueClass` can
 * be passed to customize the CSS classes of the label and value elements,
 * respectively.
 *
 * @param {React.ReactNode|string} header - The header to be displayed.
 * @param {Function} renderValue - A function that returns the value to be
 *   displayed.
 * @param {Function} renderHeader - An optional function that returns the
 *   header to be displayed.
 * @param {string} wrapperClass - An optional class name to be applied to the
 *   outermost `div` element.
 * @param {string} labelClass - An optional class name to be applied to the
 *   label element.
 * @param {string} valueClass - An optional class name to be applied to the
 *   value element.
 */

import { renderInfoValue } from 'utils/helpers';

export type TaskInfoProps = {
  header?: React.ReactNode;
  value?: React.ReactNode;
  renderValue?: () => React.ReactNode;
  renderHeader?: () => React.ReactNode;
  fallback?: React.ReactNode;
  testId?: string;
  wrapperClass?: string;
  labelClass?: string;
  valueClass?: string;
  children?: React.ReactNode;
  wrapperStyle?: React.CSSProperties;
};

const TaskInfo = ({
  header,
  value,
  renderValue,
  renderHeader,
  fallback = '-',
  testId,
  wrapperClass,
  labelClass,
  valueClass,
  children,
  wrapperStyle,
}: TaskInfoProps) => {
  const classNames = {
    wrapper: `info-item ${wrapperClass ?? ''}`,
    label: `info-label ${labelClass ?? ''}`,
    value: `info-value ${valueClass ?? ''}`,
  };

  return (
    <div className={classNames.wrapper} style={wrapperStyle}>
      {renderHeader?.() ?? <label className={classNames.label}>{header}</label>}
      <div className={classNames.value} data-testid={testId}>
        {renderValue?.() ??
          renderInfoValue(value ?? children, fallback as string)}
      </div>
    </div>
  );
};

export default TaskInfo;
